<template>
  <v-container
    class="pa-0"
    fluid>
    <v-form
      ref="product-form"
      @submit.prevent="onSave()">
      <div class="product-toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
        <span v-if="!isEditProduct">
          Create Product
        </span>
        <span v-else> Update : {{ formData.title }}</span>
        <div
          class="d-flex flex-row align-center"
          style="gap: 0.625rem;">
          <v-btn
            v-if="isEditProduct"
            color="error"
            outlined
            rounded
            small
            :loading="loading"
            @click="confirmDeleteProduct()">
            {{ $t('button.Remove') }}
          </v-btn>
          <v-btn
            color="success"
            rounded
            small
            :loading="loading"
            type="submit"
          >
            {{ $t('button.Save') }}
          </v-btn>
        </div>
      </div>
      <div class="row pa-0 ma-0">
        <v-col
          cols="12"
          class="px-2"
          md="6">
          <ProductSelector @select="onSelectProduct" />
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="product-form px-2"
        >
          <v-col
            class="card-container elevation-3"
            cols="12"
            :class="{ 'card-container-error': isProductCategoryNotMatch }">
            <v-row>
              <v-col
                cols="12">
                <div
                  class="card-title"
                  :class="{ 'text-red': isProductCategoryNotMatch }">
                  Selected Product
                </div>
              </v-col>
              <v-col
                v-for="(product, index) in selectedProducts"
                :key="`product-${index}`"
                cols="12">
                <ProductCard
                  v-bind="product"
                  remove-able
                  @remove="selectedProducts.splice(index, 1)" />
              </v-col>
              <v-col
                v-if="isProductCategoryNotMatch"
                class="text-red">
                <span>Category ของสินค้าไม่ตรงกัน / Product categories do not match.</span>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            class="card-container elevation-3 mt-4"
            cols="12">
            <v-row no-gutters>
              <v-col cols="12">
                <div class="card-title">
                  Basic Information
                </div>
              </v-col>
              <v-col
                cols="12"
                class="text-bold mt-2">
                Product Images
              </v-col>
              <v-col
                cols="12"
                class="mt-2 mb-6">
                <gw-drop-file
                  v-model="formData.mainImages"
                  accept="image/*"
                  :loading="loading"
                  :img-aspect-ratio="0.75"
                  s3-path="/tiktok-product-images"
                  multiple
                  :limit="9"
                  required />
              </v-col>
              <v-col
                cols="12"
                class="mt-2">
                <v-text-field
                  v-model="formData.title"
                  label="Product title"
                  :rules="[
                    ...rules.required,
                    ...rules.minLength(25),
                    ...rules.maxLength(255)
                  ]"
                  dense
                  outlined />
              </v-col>
              <v-col
                cols="12"
                class="mt-2">
                <CategoriesTree
                  v-model="formData.categoryId"
                  :rules="rules.required"
                  :recommend-id="recommendCategoryId"
                  :disabled="isEditProduct || loading" />
              </v-col>
              <v-col
                cols="12"
                class="mt-2">
                <v-autocomplete
                  v-model="formData.brandId"
                  :disabled="isEditProduct"
                  :items="brands"
                  item-text="name"
                  item-value="id"
                  label="Brand"
                  :rules="rules.required"
                  dense
                  outlined />
              </v-col>
              <v-col
                cols="12"
                class="mt-2">
                <AttributesForm
                  v-model="formData.productAttributes"
                  :attributes="productAttributes"
                  :loading="loading" />
              </v-col>
            </v-row>
          </v-col>
          <v-col
            class="card-container elevation-3 mt-4"
            cols="12">
            <div class="title">
              Product details
            </div>
            <div class="mt-4">
              <VueEditor
                v-model="formData.description"
                :editor-toolbar="customToolbar">
              </VueEditor>
              <v-text-field
                :value="formData.description"
                class="hidden-field"
                label="Product description"
                :rules="[
                  ...rules.required,
                  ...rules.maxLength(9500)
                ]" />
            </div>
          </v-col>
          <v-col
            class="card-container elevation-1 mt-4"
            cols="12">
            <div class="title">
              Sales information ({{ skus.length }} items)
            </div>
            <div
              v-if="skus && skus.length !== 0"
              class="mt-4">
              <v-simple-table class="elevation-1">
                <thead>
                  <tr>
                    <th
                      class="text-center"
                      scope="col">
                      Image
                    </th>
                    <th
                      class="text-center"
                      scope="col">
                      Seller SKU
                    </th>
                    <th
                      class="text-center"
                      scope="col">
                      {{ option1 }}
                    </th>
                    <th
                      class="text-center"
                      scope="col">
                      {{ option2 }}
                    </th>
                    <th
                      class="text-right"
                      scope="col">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(sku, skuIndex) in skus"
                    :key="`sku-${skuIndex}`">
                    <td
                      class="py-1"
                      style="width: 200px">
                      <gw-drop-file
                        v-if="sku.salesAttributes.length && sku.salesAttributes[0].skuImg"
                        :loading="loading"
                        accept="image/*"
                        caption="click to upload"
                        s3-path="/tiktok-product-sku"
                        :value="sku.salesAttributes[0].skuImg.url"
                        :limit="1"
                        required
                        @input="(event) => {
                          sku.salesAttributes[0].skuImg.url = event;
                          sku.salesAttributes[0].skuImg.uri = null
                        }" />
                    </td>
                    <td class="text-center">
                      <div>
                        <span>
                          {{ sku.sellerSku }}
                        </span> <br>
                        <span
                          v-if="sku.id"
                          class="grey--text">
                          {{ sku.id }}
                        </span>
                      </div>
                    </td>
                    <td
                      class="text-center capitalize"
                      style="width: 400px">
                      <v-text-field
                        v-if="sku.salesAttributes[0]"
                        v-model="sku.salesAttributes[0].valueName"
                        :disabled="!!sku.id"
                        :rules="[
                          ...rules.required,
                          isDuplicateOption(sku)
                        ]"
                        dense
                        outlined />
                    </td>
                    <td
                      class="text-center"
                      style="width: 200px">
                      <v-text-field
                        v-if="sku.salesAttributes[1]"
                        v-model="sku.salesAttributes[1].valueName"
                        :disabled="!!sku.id"
                        :rules="[
                          ...rules.required,
                          isDuplicateOption(sku)
                        ]"
                        dense
                        outlined />
                    </td>
                    <td class="text-right">
                      {{ sku.price.amount | showFullPriceFormat() }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-col>
          <v-col class="card-container elevation-3 mt-4">
            <div class="title mb-2">
              Size chart
            </div>
            <gw-drop-file
              v-model="formData.sizeChart"
              :loading="loading"
              accept="image/*"
              s3-path="/tiktok-product-size-charts"
              :limit="1" />
          </v-col>
          <v-col class="card-container elevation-3 mt-4">
            <div class="title">
              Shipping Details
            </div>
            <div class="text-bold mt-8">
              Package dimension
            </div>
            <v-row class="mt-1">
              <v-col
                cols="12"
                md="4">
                <v-text-field
                  v-model="formData.packageDimensions.width"
                  label="Width"
                  :suffix="units[formData.packageDimensions.unit] || units.CENTIMETER"
                  :rules="rules.number"
                  dense
                  outlined />
              </v-col>
              <v-col
                cols="12"
                md="4">
                <v-text-field
                  v-model="formData.packageDimensions.height"
                  label="Height"
                  :suffix="units[formData.packageDimensions.unit] || units.CENTIMETER"
                  :rules="rules.number"
                  dense
                  outlined />
              </v-col>
              <v-col
                cols="12"
                md="4">
                <v-text-field
                  v-model="formData.packageDimensions.length"
                  label="Length"
                  :suffix="units[formData.packageDimensions.unit] || units.CENTIMETER"
                  :rules="rules.number"
                  dense
                  outlined />
              </v-col>
            </v-row>
            <div class="text-bold mt-8">
              Package weight
            </div>
            <v-row class="mt-1">
              <v-col cols="12">
                <v-text-field
                  v-model="formData.packageWeight.value"
                  label="Weight"
                  :suffix="units[formData.packageWeight.unit] || units.GRAM"
                  :rules="rules.number"
                  dense
                  outlined />
              </v-col>
            </v-row>
          </v-col>
        </v-col>
      </div>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { VueEditor } from 'vue2-editor'
import humps from 'humps'
import TiktokProvider from '@/resources/TiktokProvider.js'
import ProductProvider from '@/resources/ProductProvider.js'
import CategoriesTree from '../components/CategoriesTree.vue'
import AttributesForm from '../components/AttributesForm.vue'
import ProductSelector from '../components/ProductSelector.vue'
import ProductCard from '../components/ProductCard.vue'
import SizeValues from '@/assets/js/SizeValues'

const TiktokService = new TiktokProvider()
const ProductService = new ProductProvider()

export default {
  components: {
    CategoriesTree,
    AttributesForm,
    ProductCard,
    ProductSelector,
    VueEditor
  },
  data () {
    return {
      loading: false,
      units: {
        CENTIMETER: 'CM',
        METER: 'M',
        INCH: 'IN',
        GRAM: 'GRAM',
        KILOGRAM: 'KG'
      },
      attributes: [],
      selectedProducts: [],
      skus: [],
      recommendCategoryId: '',
      tiktokData: {},
      allImages: [],
      formData: {
        id: null,
        saveMode: 'AS_DRAFT',
        description: '',
        categoryId: '',
        brandId: '',
        mainImages: [],
        skus: [],
        title: '',
        isCodAllowed: false,
        packageDimensions: {
          length: null,
          width: null,
          height: null,
          unit: 'CENTIMETER'
        },
        productAttributes: [],
        packageWeight: {
          value: null,
          unit: 'GRAM'
        },
        video: null,
        deliveryOptionIds: [],
        sizeChart: null,
        isNotForSale: false,
        categoryVersion: 'v2',
        shippingInsuranceRequirement: 'OPTIONAL',
        minimumOrderQuantity: 1
      },
      rules: {
        required: [
          (v) => !!v || 'This field is required.'
        ],
        number: [
          (v) => !!v || 'This field is required.',
          // (v) => /^[+-]?([0-9]*[.])?[0-9]+$/.test(v) || 'This field must be valid number.',
          (v) => /^[+-]?\d+$/.test(v) || 'This field must be valid number.',
          (v) => v > 0 || 'This field must be positive number.'
        ],
        minLength: (length = 25) => ([
          (v) => (!!v && v.length >= length) || `This field must be at least ${length} characters`
        ]),
        maxLength: (length = 255) => ([
          (v) => (!!v && v.length <= length) || `This field must be at most ${length} characters`
        ])
      },
      customToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        ['clean']
      ]
    }
  },
  computed: {
    brands () {
      const store = this.$store.getters['Store/store']

      return [

        {
          id: '7257459359897372422',
          name: 'Gentlewoman',
          disabled: store.id !== '5e3548c2d32cb12606a34fb8'
        },
        {
          id: '7032166944405227265',
          name: 'Matter Makers',
          disabled: store.id !== '645a1d00765d5519beeb97f6'
        }
      ]
    },
    isProductCategoryNotMatch () {
      const initialCategory = this.selectedProducts[0]?.productPrototype.category || null
      return this.selectedProducts.some((product) => product.productPrototype.category !== initialCategory)
    },
    brandName () {
      return this.brands.find((brand) => brand.id === this.formData.brandId)?.name || 'Gentlewoman'
    },
    isEditProduct () {
      return this.$route.name === 'TiktokEditProduct' && !!this.$route.params.productId
    },
    option1 () {
      return this.skus[0]?.salesAttributes[0]?.name || '-'
    },
    option2 () {
      return this.skus[0]?.salesAttributes[1]?.name || ''
    },
    productAttributes () {
      return this.attributes.filter((attribute) => attribute.type === 'PRODUCT_PROPERTY').sort((a, b) => Number(a.id) - Number(b.id))
    },
    editProductId () {
      return this.$route.params.productId || null
    },
    colorAttribute () {
      if (this.isEditProduct) {
        const attr = this.tiktokData?.skus?.[0]?.salesAttributes?.[0]
        return {
          id: attr?.id || null,
          name: attr?.name || 'Color'
        }
      }
      const colorAttr = this.attributes.find((v) => v.id === '100000' || v.name === 'สี' || v.name === 'Color')

      return colorAttr || this.attributes.find((v) => v.id === '100009') || {
          id: null,
          name: 'Option'
        }
    },
    sizeAttribute () {
      if (this.isEditProduct) {
        const attr = this.tiktokData?.skus?.[0]?.salesAttributes?.[1]
        return {
          id: attr?.id || null,
          name: attr?.name || 'Size'
        }
      }
      const sizeAttr = this.attributes.find((v) => v.id === '100007' || v.name === 'ขนาด' || (v.name || '').toUpperCase() === 'SIZE') || null

      return sizeAttr
        || this.attributes.find((v) => v.id === '100089' || v.name === 'ข้อมูลจำเพาะ' || v.name === 'Specification')
        || {
          id: null,
          name: 'Size'
        }
    }
  },
  watch: {
    'selectedProducts': {
      handler () {
        if (this.selectedProducts.length === 1 && !this.isEditProduct) {
          this.getRecommendCategory()
        }
        this.buildSkus()
      },
      deep: true
    },
    'formData.categoryId': {
      handler () {
        this.getAttributeList()
      },
      deep: true
    }
  },
  mounted () {
    this.initialize()
  },
  methods: {
    ...mapActions({
      setErrorPage: 'Components/setErrorPage',
      setSnackbar: 'Components/setSnackbar'
    }),
    sortSize (skus) {
      return [...skus].sort((a, b) => SizeValues(a.size.name) - SizeValues(b.size.name))
    },
    buildSkus () {
      const previousSkus = this.skus
      this.skus = this.selectedProducts.reduce((acc, product) => [...acc, ...this.sortSize(product.skus).map((sku) => {
        const salesAttributes = []
        if (this.colorAttribute) {
          salesAttributes.push({
            id: this.colorAttribute.id,
            name: this.colorAttribute.name,
            valueId: null,
            valueName: sku.color.name,
            skuImg: { url: sku?.images[0] || null },
            supplementarySkuImages: null
          })
        }

        if (this.sizeAttribute) {
          salesAttributes.push({
            id: this.sizeAttribute.id,
            name: this.sizeAttribute.name,
            valueId: null,
            valueName: sku.size.name
          })
        }
        const tiktokSku = this.formData.skus.find((ttSku) => ttSku.gwSku.skuId === sku.id)
        const draftSku = previousSkus.find((v) => v.gwSku.skuId === sku.id && !v.id)

        return {
          id: tiktokSku?.id || null,
          salesAttributes: tiktokSku?.salesAttributes || draftSku?.salesAttributes || salesAttributes,
          sellerSku: sku.code,
          price: {
            amount: `${sku.price}`,
            currency: 'THB'
          },
          gwSku: {
            productId: product.id,
            skuId: sku.id,
            model: product.model,
            code: sku.code,
            color: sku.color.name,
            size: sku.size.name,
            price: sku.price
          }
        }
      })], [])
    },
    isDuplicateOption (sku) {
      const duplicateOption = this.skus.filter((sk) => {
        const option1 = sk.salesAttributes[0]?.valueName || ''
        const option2 = sk.salesAttributes[1]?.valueName || ''

        const skuOption1 = sku.salesAttributes[0]?.valueName || ''
        const skuOption2 = sku.salesAttributes[1]?.valueName || ''
        return !!(option1.toUpperCase() === skuOption1.toUpperCase() && option2.toUpperCase() === skuOption2.toUpperCase())
      })

      if (duplicateOption.length >= 2) return 'Duplicate Options'

      return true
    },
    async initialize () {
      this.loading = true
      try {
        if (this.isEditProduct) {
          await this.getProductDetail()
          await this.getAttributeList()
        }
      } catch (error) {
        this.setErrorPage({
          code: error.code,
          message: error.message
        })
        console.error('initialize', error)
      } finally {
        this.loading = false
      }
    },
    async getRecommendCategory () {
      if (this.loading === true) return
      try {
        this.loading = true
        const { data } = await TiktokService.getRecommendCategoryList({
          brand: this.brandName,
          productTitle: this.formData.title,
          images: this.formData.mainImages.filter((_, index) => index <= 3).map((v) => ({ url: v })),
          categoryVersion: 'v2'
        })
        this.recommendCategoryId = data[data.length - 1]?.id || null
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
    }
    },
    async getProductDetail () {
      const { data } = await TiktokService.getProductDetail(this.editProductId, { brand: this.brandName })
      const description = data.description.replaceAll('<p><br></p>', '<br>').replaceAll('<br>', '<p><br></p>')
      this.formData = {
        ...data,
        description,
        mainImages: data.mainImages.map((mi) => mi.url),
        sizeChart: data.sizeChart?.image?.url || null
      }

      const { data: foundProducts } = await ProductService.getManyProductByModel({ modelIds: data.models })
      this.selectedProducts = foundProducts
      this.tiktokData = data
    },
    async getAttributeList () {
      const { data } = await TiktokService.getAttributeList({ brand: this.brandName, category: this.formData.categoryId })
      this.attributes = humps.camelizeKeys(data?.attributes || []).map((v) => ({
        ...v,
        values: v?.values || []
      }))

      const mappedAttributes = this.attributes.map((v) => {
        const productAttribute = this.formData.productAttributes.find((p) => p.id === v.id)

        return {
          id: v.id,
          name: v.name,
          isCustomizable: v.isCustomizable,
          isMultipleSelection: v.isMultipleSelection,
          values: v.isMultipleSelection ? (productAttribute?.values || []) : (productAttribute?.values[0] || null),
          options: v.values
        }
      })

      this.formData.productAttributes = [...mappedAttributes].sort((a, b) => Number(a.id) - Number(b.id))
      this.buildSkus()
    },
    onSelectProduct (product) {
      if (this.selectedProducts.length === 0) {
        this.formData.title = product.name
      }

      if (this.selectedProducts.some((v) => v.id === product.id)) {
        this.setSnackbar({
          value: true,
          type: 'error',
          message: 'Duplicate product.'
        })
        return
      }

      this.selectedProducts.push(product)
      this.formData.mainImages = [...this.formData.mainImages, ...product.photoUrls]
    },
    getAttributeValueByName (attributes, attributeName) {
      const attribute = attributes.find((v) => v.name.toUpperCase() === attributeName.toUpperCase())

      return attribute?.valueName || ''
    },
    getAttributeValue (attributes, attributeId) {
      const attribute = attributes.find((v) => v.id === attributeId)

      return attribute?.valueName || ''
    },
    confirmDeleteProduct () {
      this.$store.dispatch('Components/setModal', {
        value: true,
        title: 'Remove Product',
        message: `Do you want to remove the  ${this.formData.title} ?`,
        confirmText: 'REMOVE',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'CANCEL',
        onConfirm: () => this.deleteProduct()
      })
    },
    async deleteProduct () {
      this.loading = true
      try {
        await TiktokService.deleteProduct(this.editProductId, { brand: this.brandName })
        this.$router.push({ name: 'TiktokProductsList' })
        this.setSnackbar({
          value: true,
          type: 'success',
          message: 'Tiktok Product deleted successfully'
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          type: 'error',
          message: `Delete product error msg ${error.message}.`
        })
        console.error('deleteProduct', error)
      } finally {
        this.loading = false
      }
    },
    async onSave () {
      const isValid = await this.$refs['product-form'].validate()

      if (!isValid || this.saving || this.isProductCategoryNotMatch || this.formData.mainImages.length > 9) {
        this.setSnackbar({
          value: true,
          type: 'error',
          message: 'Please check the data.'
        })
        return
      }

      try {
        this.loading = true
        if (this.isEditProduct) {
          const payload = {
            ...this.formData,
            skus: this.skus,
            brand: this.brandName,
            shippingInsuranceRequirement: this.formData.shippingInsuranceRequirement || 'OPTIONAL',
            productAttributes: this.formData.productAttributes
              .filter((pa) => {
                if (!pa || !pa?.values) {
                  return false
                }

                if (pa.isMultipleSelection) {
                  return pa.values.length > 0
                }

                return Object.keys(pa.values).length > 0
              })
              .map((pa) => ({
                ...pa,
                values: pa.isMultipleSelection ? pa.values : [pa.values]
              })),
            sizeChart: this.formData.sizeChart ? {
              image: {
                url: this.formData.sizeChart,
                uri: this.tiktokData?.sizeChart?.image?.url && this.formData.sizeChart === this.tiktokData.sizeChart.image.url
                  ? this.tiktokData.sizeChart.image.uri : null
              }
            } : null,
            mainImages: this.formData.mainImages.map((v) => {
              const foundImage = this.tiktokData.mainImages.find((mi) => mi.url === v)
              return {
                uri: foundImage?.uri || null,
                url: v
              }
            })
          }

          await TiktokService.updateProduct(this.editProductId, payload)

          this.attributes = []
          this.initialize()

          this.setSnackbar({
            value: true,
            type: 'success',
            message: 'Tiktok Product updated'
          })
        } else {
          const payload = {
            ...this.formData,
            skus: this.skus,
            brand: this.brandName,
            productAttributes: this.formData.productAttributes
              .filter((pa) => {
                if (!pa || !pa?.values) {
                  return false
                }

                if (pa.isMultipleSelection) {
                  return pa.values.length > 0
                }

                return Object.keys(pa.values).length > 0
              })
              .map((pa) => ({
                ...pa,
                values: pa.isMultipleSelection ? pa.values : [pa.values]
              })),
            sizeChart: this.formData.sizeChart ? {
              image: {
                url: this.formData.sizeChart
              }
            } : null,
            mainImages: this.formData.mainImages.map((v) => ({
              url: v
            }))
          }

          const { data: { data } } = await TiktokService.createProduct(payload)

          this.recommendCategoryId = ''
          this.attributes = []
          this.$router.replace({ name: 'TiktokEditProduct', params: { productId: data.productId } })
          this.initialize()

          this.setSnackbar({
            value: true,
            type: 'success',
            message: 'Tiktok Product created successfully'
          })
        }
      } catch (error) {
        console.error('onSave', error)
        this.setSnackbar({
          value: true,
          type: 'error',
          message: error.message
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.card-container-error {
  border: 2px dashed red;
}
.text-red {
  color: red !important;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
}
.h-full {
  height: 100%;
}
.text-sm {
  font-size: 12px !important;
}
.product-select-image-wrapper {
  width: 50px;
}
.product-select-wrapper {
  height: 100px;
  width: 100%;
  border-radius: 6px;
  border: 1px solid #cfcfcf;
  padding: 12px;
  display: flex;
}
.cursor-pointer {
  cursor: pointer;
}
.thumbnail-wrapper {
  width: 300px;
  height: 300px;
  border: 1px solid #cfcfcf;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.images-wrapper {
  width: 148px;
  height: 148px;
  border: 1px solid #cfcfcf;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 4px;
  margin-bottom: 4px;
}
.search-product-btn {
  height: 100px;
  width: 100%;
  border-radius: 6px;
  border: 1px dashed #cfcfcf;
  color: #969696;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}
.product-toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.product-toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.product-form {
  max-height: calc(100vh - 160px);
  overflow-y: auto;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
</style>
